import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Flame, Camera, MapPin, Plus, X, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProfileSetup() {
  const navigate = useNavigate();
  const { user, token, updateUser } = useAuth();
  const fileInputRef = useRef(null);
  
  const [name, setName] = useState(user?.name || '');
  const [bio, setBio] = useState(user?.bio || '');
  const [age, setAge] = useState(user?.age || '');
  const [gender, setGender] = useState(user?.gender || '');
  const [lookingFor, setLookingFor] = useState(user?.looking_for || '');
  const [photos, setPhotos] = useState(user?.photos || []);
  const [location, setLocation] = useState({ lat: user?.latitude, lng: user?.longitude });
  const [locationLoading, setLocationLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Get user's location
  const getLocation = () => {
    setLocationLoading(true);
    setError('');
    
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by your browser');
      setLocationLoading(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocation({
          lat: position.coords.latitude,
          lng: position.coords.longitude
        });
        setLocationLoading(false);
      },
      (err) => {
        setError('Unable to get your location. Please enable location access.');
        setLocationLoading(false);
      }
    );
  };

  // Handle photo upload
  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (photos.length >= 6) {
      setError('Maximum 6 photos allowed');
      return;
    }

    setUploading(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('file', file);

      const { data } = await axios.post(`${API}/profile/photo`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      setPhotos([...photos, data.photo_url]);
    } catch (e) {
      setError('Failed to upload photo');
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = (index) => {
    setPhotos(photos.filter((_, i) => i !== index));
  };

  // Save profile
  const handleSave = async () => {
    setError('');

    if (!name || !age || !gender) {
      setError('Please fill in all required fields');
      return;
    }

    if (!location.lat || !location.lng) {
      setError('Please enable location to find local matches');
      return;
    }

    setSaving(true);

    try {
      const { data } = await axios.put(`${API}/profile`, {
        name,
        bio,
        age: parseInt(age),
        gender,
        looking_for: lookingFor,
        latitude: location.lat,
        longitude: location.lng,
        photos
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      updateUser(data.user);
      navigate('/app');
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] pb-8">
      {/* Header */}
      <header className="px-6 py-6 flex items-center gap-2">
        <Flame className="w-6 h-6 text-rose-500" />
        <span className="font-outfit text-xl font-bold text-white">Set Up Profile</span>
      </header>

      <main className="px-6 max-w-lg mx-auto">
        {error && (
          <div data-testid="profile-error" className="mb-6 p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
            {error}
          </div>
        )}

        {/* Photos */}
        <section className="mb-8">
          <Label className="text-zinc-400 mb-3 block">Photos (up to 6)</Label>
          <div className="grid grid-cols-3 gap-3">
            {photos.map((photo, index) => (
              <div key={index} className="relative aspect-square rounded-xl overflow-hidden">
                <img src={photo} alt="" className="w-full h-full object-cover" />
                <button
                  data-testid={`remove-photo-${index}`}
                  onClick={() => removePhoto(index)}
                  className="absolute top-2 right-2 w-6 h-6 rounded-full bg-black/60 flex items-center justify-center text-white"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
            {photos.length < 6 && (
              <button
                data-testid="add-photo-btn"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                className="aspect-square rounded-xl border-2 border-dashed border-zinc-700 flex flex-col items-center justify-center text-zinc-500 hover:border-rose-500 hover:text-rose-500 transition-colors"
              >
                {uploading ? (
                  <Loader2 className="w-6 h-6 animate-spin" />
                ) : (
                  <>
                    <Plus className="w-6 h-6 mb-1" />
                    <span className="text-xs">Add</span>
                  </>
                )}
              </button>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            onChange={handlePhotoUpload}
            className="hidden"
          />
        </section>

        {/* Basic Info */}
        <section className="space-y-5 mb-8">
          <div>
            <Label htmlFor="name" className="text-zinc-400">Name *</Label>
            <Input
              id="name"
              data-testid="profile-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="mt-2 bg-zinc-900 border-zinc-800 text-white h-12"
            />
          </div>

          <div>
            <Label htmlFor="age" className="text-zinc-400">Age *</Label>
            <Input
              id="age"
              data-testid="profile-age-input"
              type="number"
              min="18"
              max="99"
              value={age}
              onChange={(e) => setAge(e.target.value)}
              className="mt-2 bg-zinc-900 border-zinc-800 text-white h-12"
            />
          </div>

          <div>
            <Label className="text-zinc-400">Gender *</Label>
            <Select value={gender} onValueChange={setGender}>
              <SelectTrigger data-testid="gender-select" className="mt-2 bg-zinc-900 border-zinc-800 text-white h-12">
                <SelectValue placeholder="Select gender" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800">
                <SelectItem value="male">Male</SelectItem>
                <SelectItem value="female">Female</SelectItem>
                <SelectItem value="non-binary">Non-binary</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-zinc-400">Looking For</Label>
            <Select value={lookingFor} onValueChange={setLookingFor}>
              <SelectTrigger data-testid="looking-for-select" className="mt-2 bg-zinc-900 border-zinc-800 text-white h-12">
                <SelectValue placeholder="Select preference" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800">
                <SelectItem value="men">Men</SelectItem>
                <SelectItem value="women">Women</SelectItem>
                <SelectItem value="everyone">Everyone</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="bio" className="text-zinc-400">About You</Label>
            <Textarea
              id="bio"
              data-testid="profile-bio-input"
              value={bio}
              onChange={(e) => setBio(e.target.value)}
              placeholder="Tell potential matches about yourself..."
              rows={3}
              className="mt-2 bg-zinc-900 border-zinc-800 text-white resize-none"
            />
          </div>
        </section>

        {/* Location */}
        <section className="mb-10">
          <Label className="text-zinc-400 mb-3 block">Location *</Label>
          <Button
            data-testid="get-location-btn"
            variant="outline"
            onClick={getLocation}
            disabled={locationLoading}
            className={`w-full h-12 border-zinc-700 ${location.lat ? 'text-green-500 border-green-500/30' : 'text-zinc-400'}`}
          >
            {locationLoading ? (
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
            ) : (
              <MapPin className="w-5 h-5 mr-2" />
            )}
            {location.lat ? 'Location Set!' : 'Enable Location'}
          </Button>
          <p className="text-zinc-600 text-xs mt-2">
            Required to find matches within 25 miles
          </p>
        </section>

        {/* Save Button */}
        <Button
          data-testid="save-profile-btn"
          onClick={handleSave}
          disabled={saving}
          className="w-full h-14 bg-rose-600 hover:bg-rose-500 text-white font-semibold rounded-full text-lg"
        >
          {saving ? (
            <>
              <Loader2 className="w-5 h-5 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            'Save & Continue'
          )}
        </Button>
      </main>
    </div>
  );
}
